import { escapeHtml } from '@/lib/format';
import { todayIso } from '@/lib/availability';

const DAY_LABELS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTH_LABELS_IT = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(year: number, month0: number, day: number): string {
  return `${year}-${pad(month0 + 1)}-${pad(day)}`;
}

function parseIso(iso: string): { year: number; month0: number; day: number } {
  const [y, m, d] = iso.split('-').map((p) => Number.parseInt(p, 10));
  return { year: y ?? 0, month0: (m ?? 1) - 1, day: d ?? 1 };
}

/** First weekday (Monday=0..Sunday=6) for the 1st of month. */
function firstWeekdayMonFirst(year: number, month0: number): number {
  const jsDay = new Date(year, month0, 1).getDay(); // 0=Sun..6=Sat
  return (jsDay + 6) % 7;
}

function daysInMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate();
}

export interface CalendarParams {
  /** Currently selected start date (yyyy-mm-dd). */
  value: string;
  /** Set of bookable yyyy-mm-dd dates that should be highlighted as selectable. */
  available: Set<string>;
  /** Earliest selectable date (yyyy-mm-dd). Defaults to today. */
  min?: string;
  /** Latest selectable date (yyyy-mm-dd). */
  max?: string;
  /** Year-month to display (yyyy-mm). Defaults to value's month. */
  view?: string;
}

/**
 * Render an inline month-view calendar. The output is keyed with data-action
 * attributes so the caller can wire interactions:
 *   - data-action="prev" / "next"           — month navigation
 *   - data-action="pick" data-date="yyyy-mm-dd" — pick a date
 */
export function renderCalendar(params: CalendarParams): string {
  const today = todayIso();
  const minIso = params.min ?? today;
  const maxIso = params.max ?? '';
  const value = params.value;
  const view = params.view ?? value.slice(0, 7) ?? today.slice(0, 7);
  const [vYear, vMonth] = view.split('-').map((p) => Number.parseInt(p, 10));
  const year = Number.isFinite(vYear) ? (vYear as number) : new Date().getFullYear();
  const month0 = Number.isFinite(vMonth) ? (vMonth as number) - 1 : new Date().getMonth();

  const start = firstWeekdayMonFirst(year, month0);
  const totalDays = daysInMonth(year, month0);
  const cells: string[] = [];

  // Leading blanks
  for (let i = 0; i < start; i += 1) {
    cells.push('<div class="cal-cell cal-blank"></div>');
  }

  for (let day = 1; day <= totalDays; day += 1) {
    const iso = toIso(year, month0, day);
    const isPast = iso < minIso;
    const isAfterMax = maxIso ? iso > maxIso : false;
    const isAvail = params.available.has(iso) && !isPast && !isAfterMax;
    const isSelected = iso === value;
    const isToday = iso === today;

    const classes = ['cal-cell'];
    if (isPast || isAfterMax) classes.push('cal-disabled');
    else if (isAvail) classes.push('cal-avail');
    if (isSelected) classes.push('cal-selected');
    if (isToday) classes.push('cal-today');

    const disabled = !isAvail;
    cells.push(`
      <button
        type="button"
        class="${classes.join(' ')}"
        data-action="pick"
        data-date="${iso}"
        ${disabled ? 'disabled' : ''}
        aria-pressed="${isSelected ? 'true' : 'false'}"
      >${day}</button>
    `);
  }

  // Compute prev/next navigation bounds.
  const prevMonth = month0 === 0 ? toIso(year - 1, 11, 1) : toIso(year, month0 - 1, 1);
  const nextMonth = month0 === 11 ? toIso(year + 1, 0, 1) : toIso(year, month0 + 1, 1);

  const prevMonthLastDay =
    month0 === 0
      ? toIso(year - 1, 11, daysInMonth(year - 1, 11))
      : toIso(year, month0 - 1, daysInMonth(year, month0 - 1));
  const nextMonthFirstDay = nextMonth;

  const canGoPrev = prevMonthLastDay >= minIso;
  const canGoNext = !maxIso || nextMonthFirstDay <= maxIso;

  return `
    <div class="cal">
      <div class="cal-hd">
        <button class="cal-nav" type="button" data-action="prev" data-view="${escapeHtml(prevMonth.slice(0, 7))}" ${canGoPrev ? '' : 'disabled'} aria-label="Mese precedente">‹</button>
        <span class="cal-title">${escapeHtml(MONTH_LABELS_IT[month0] ?? '')} ${year}</span>
        <button class="cal-nav" type="button" data-action="next" data-view="${escapeHtml(nextMonth.slice(0, 7))}" ${canGoNext ? '' : 'disabled'} aria-label="Mese successivo">›</button>
      </div>
      <div class="cal-dow">
        ${DAY_LABELS_IT.map((d) => `<div class="cal-dow-cell">${d}</div>`).join('')}
      </div>
      <div class="cal-grid">
        ${cells.join('')}
      </div>
      <div class="cal-hint">Date in <strong>magenta</strong> prenotabili</div>
    </div>
  `;
}

/** Helper to extract the yyyy-mm portion (view) from a date input. */
export function viewOf(iso: string): string {
  return iso.slice(0, 7);
}

export { parseIso };
